import React, { memo, useCallback } from 'react';
import { Box, Button } from '@betty/moonlight';
import {
  BottomSheet,
  StackNavigationProp,
  useNavigation,
} from '@betty/navigation';
import {
  activateQuest,
  selectActivatingQuestIds,
  selectIsAbandoningQuest,
} from '../../../../state';
import { useDispatch, useSelector } from '@betty/toolkit/state';
import { Quest, QuestStatus } from '../../../../services/QuestsService.schemas';

import { isValidOptInPromo } from '../../../../utils/isValidOptInPromo';
import { useHandleStartQuestClick } from '../../../../hooks/useHandleStartQuestClick/useHandleStartQuestClick';
import { QuestButtonsProps } from './QuestButton.types';
import { useTranslation } from '@betty/i18n';
import useBreakpoint from '@betty/moonlight/lib/hooks/useBreakpoint';
import { Breakpoint } from '@betty/moonlight/lib/hooks/useBreakpoint/index.types';

function QuestButtons({
  quest,
  isCompleted,
  questHasStarted,
  detailsPageView,
  selectGameView,
  selectedGame,
}: QuestButtonsProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation('quests');
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const { status, promotionId, subTypeMeta, slug } = quest;
  const activatingQuestIds = useSelector(selectActivatingQuestIds);
  const abandoningQuestIds = useSelector(selectIsAbandoningQuest(promotionId));
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === Breakpoint.MOBILE;

  const isOptedIn = status?.match(
    `${QuestStatus.OptIn}|${QuestStatus.InProgress}`,
  );

  const handleStartQuestClick = useHandleStartQuestClick({
    gameIds: subTypeMeta?.gameIds,
    questId: promotionId,
    selectedGame,
  });

  const handleMainCtaClick = useCallback(
    async (quest: Quest) => {
      if (quest.isOptedIn) {
        handleStartQuestClick();
      } else {
        try {
          await dispatch(activateQuest(promotionId)).unwrap();
        } catch (_err) {
          BottomSheet.open({
            namespace: 'common',
            type: 'Info',
            data: {
              text: t('optInErrMessage'),
            },
          });
        }
      }
    },
    [dispatch, handleStartQuestClick, promotionId, t],
  );

  const handleLearnMore = useCallback(() => {
    navigation.navigate('QuestDetails', {
      id: slug ?? promotionId,
    });
  }, [navigation, slug, promotionId]);

  const handleAbandon = useCallback(() => {
    BottomSheet.open({
      namespace: 'quests',
      type: 'abandon',
      titleNS: 'quests',
      titleKey: 'optOutOfQuestTitle',
      data: { gameId: promotionId },
    });
  }, [promotionId]);

  return (
    <Box
      gap="space-400"
      {...(detailsPageView
        ? {
            flexDirection: {
              mobile: 'column',
              tablet: 'column',
              desktop: 'row',
            },
          }
        : {})}
    >
      {!isCompleted && questHasStarted ? (
        <Button
          variant="primary"
          onPress={() => handleMainCtaClick(quest)}
          locNS="quests"
          locKey={
            isOptedIn
              ? subTypeMeta.currentAccumulation
                ? 'continueQuest'
                : 'startQuest'
              : 'activateQuest'
          }
          size={detailsPageView && !isMobile ? 'small' : 'large'}
          loading={activatingQuestIds.indexOf(promotionId) !== -1}
          disabled={
            status === QuestStatus.Locked || (selectGameView && !selectedGame)
          }
        />
      ) : null}

      {detailsPageView &&
      quest?.isOptedIn &&
      isValidOptInPromo(quest) &&
      status !== QuestStatus.Completed ? (
        <Button
          variant="secondary"
          onPress={handleAbandon}
          loading={abandoningQuestIds}
          locNS="quests"
          locKey="abandonQuest"
          size={detailsPageView && !isMobile ? 'small' : 'large'}
        />
      ) : null}

      {!detailsPageView && !selectGameView ? (
        <Button
          variant="secondary"
          onPress={handleLearnMore}
          locNS="common"
          locKey="learnMore"
          size="large"
        />
      ) : null}

      {selectGameView ? (
        <Button
          variant="secondary"
          onPress={navigation.goBack}
          locNS="common"
          locKey="cancel"
          size="large"
        />
      ) : null}
    </Box>
  );
}

export default memo(QuestButtons);
